import type { DeviceMetadata } from "./types";

const DEVICE_ID_KEY = "business-analytics.deviceId";

export type StorageKind = "local" | "session";

export function isBrowser(): boolean {
	return typeof window !== "undefined" && typeof document !== "undefined";
}

/**
 * Web Storage can throw on access alone — a sandboxed iframe or a browser configured to
 * block site data — so every path through here is guarded and degrades to "no storage".
 */
function storageFor(kind: StorageKind): Storage | null {
	if (!isBrowser()) return null;
	try {
		return kind === "local" ? window.localStorage : window.sessionStorage;
	} catch {
		return null;
	}
}

export function readStorage(kind: StorageKind, key: string): string | null {
	try {
		return storageFor(kind)?.getItem(key) ?? null;
	} catch {
		return null;
	}
}

export function writeStorage(kind: StorageKind, key: string, value: string): void {
	try {
		storageFor(kind)?.setItem(key, value);
	} catch {
		// Out of quota, or writes are blocked. Losing persistence is not worth throwing over.
	}
}

/**
 * UUID v4. `crypto.randomUUID` needs a secure context, so plain-http origins fall back to
 * `getRandomValues`, and anything without WebCrypto at all falls back to `Math.random`.
 * Keeping this here is what lets the package stay dependency-free.
 */
export function generateId(): string {
	const webCrypto = typeof crypto !== "undefined" ? crypto : undefined;

	if (typeof webCrypto?.randomUUID === "function") {
		return webCrypto.randomUUID();
	}

	const bytes = new Uint8Array(16);
	if (typeof webCrypto?.getRandomValues === "function") {
		webCrypto.getRandomValues(bytes);
	} else {
		for (let i = 0; i < bytes.length; i += 1) {
			bytes[i] = Math.floor(Math.random() * 256);
		}
	}

	bytes[6] = (bytes[6] & 0x0f) | 0x40;
	bytes[8] = (bytes[8] & 0x3f) | 0x80;

	const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Stable per browser, so it lives in localStorage rather than dying with the tab. */
function deviceId(): string | null {
	if (!isBrowser()) return null;

	let id = readStorage("local", DEVICE_ID_KEY);
	if (!id) {
		id = generateId();
		writeStorage("local", DEVICE_ID_KEY, id);
	}
	return id;
}

/** Not in TypeScript 4.9's DOM lib, and Chromium-only at runtime. */
interface UserAgentDataLike {
	platform?: string;
}

/**
 * Best effort, zero dependencies. A browser cannot report brand, manufacturer, model or
 * its own IP address, and `osVersion` needs the async high-entropy API, so all of those
 * stay null — the contract makes every one of them optional.
 */
export function deviceMetadata(): DeviceMetadata {
	const nav = isBrowser() ? navigator : undefined;
	const uaData = (nav as (Navigator & { userAgentData?: UserAgentDataLike }) | undefined)?.userAgentData;

	return {
		deviceId: deviceId(),
		platform: "WEB",
		osName: uaData?.platform ?? null,
		osVersion: null,
		deviceBrand: null,
		deviceManufacturer: null,
		deviceModel: null,
		language: nav?.language ?? null,
	};
}
